/**
 * Authoring-reachability rules — the checks that ask whether authored content
 * can ever be REACHED, as distinct from whether its ids resolve.
 *
 * @remarks
 * Split out of `semantic-validation.ts` in D53 on that distinction rather than
 * on line count alone: everything there answers "does this id exist?", and
 * these answer "will anyone ever see this?" — D50's containment graph, where
 * a cycle or an absent container strands contents, the carry declarations,
 * and the constant-condition walk. The per-item VERB rules (D47's refusals,
 * D196's authored acts) live in `authored-verb-rules.ts` since the cap.
 *
 * Both follow the D31 precedent: content that can never play is a blocking
 * defect at load time, not a silent nothing at run time.
 *
 * `semantic-validation.ts` re-exports both, so no caller changed.
 *
 * @packageDocumentation
 */

import { type ConditionDefinition, containerItemIdOf, type ContentPackDefinition, type EffectDefinition, type ItemDefinition } from '@orpheus/content-schema';

import { validateBodyPockets } from './body-pocket-validation.js';
import { deathEndingIdsOf } from './combat-validation.js';
import { effectSourcesOf } from './effect-sources.js';
import { type ReferenceBrokenFailure } from './load-failure.js';
import { brokenReference } from './semantic-validation.js';
import { walkDocumentObjects } from './structural-walk.js';

/**
 * D50: containment must describe a world that can exist.
 *
 * @remarks
 * Three checks, and the shape of the model means there are only three. Because
 * containment is authored on the CONTAINED item, "an item is in exactly one
 * container" is true by construction and needs no rule at all — the
 * container-side draft would have needed one.
 *
 * - The named container must be a declared item.
 * - No containment CYCLE, directly or transitively. A locker inside a drawer
 *   inside that locker is not a world; it is a hang waiting to happen in
 *   catalog derivation.
 * - A contained item must not ALSO be placed in a room. Two homes is exactly
 *   the ambiguity `containedIn` exists to remove, and an author who does it
 *   has almost certainly forgotten to delete the placement.
 *
 * D240: a thing a BODY carries is judged by `validateBodyPockets`, whose
 * failures join these.
 *
 * Effects that name a door get the same treatment for the same reason D31/D47
 * gave authored lines: an `open_container` aimed at something with no
 * `openable` fires into nothing, which is content that can never be reached.
 *
 * @param contentPack - The parsed pack
 * @returns One failure per broken relationship, all collected
 */
export const validateContainment = (contentPack: ContentPackDefinition): readonly ReferenceBrokenFailure[] => {
	const failures: ReferenceBrokenFailure[] = [];
	const itemsById = new Map(contentPack.items.map((item) => [item.itemId, item]));
	const placedItemIds = new Set(contentPack.rooms.flatMap((room) => room.itemPlacements));

	contentPack.items.forEach((item, itemIndex) => {
		// D240: a body's pockets have their own rules (`body-pocket-validation.ts`), joined below.
		const containerItemId = containerItemIdOf(item.containedIn);

		if (containerItemId === undefined) {
			return;
		}

		const referencePath = `items[${String(itemIndex)}].containedIn.containerItemId`;

		if (!itemsById.has(containerItemId)) {
			failures.push(brokenReference(referencePath, containerItemId, 'contained by an undeclared item'));

			return;
		}

		if (placedItemIds.has(item.itemId)) {
			failures.push(
				brokenReference(
					`items[${String(itemIndex)}].containedIn`,
					item.itemId,
					'this item is inside a container AND placed in a room — it cannot have two homes',
				),
			);
		}

		if (hasContainmentCycle(item.itemId, itemsById)) {
			failures.push(brokenReference(referencePath, containerItemId, 'containment cycle — this thing contains itself'));
		}
	});

	failures.push(...validateBodyPockets(contentPack));

	// The binary-state effect rule walks EVERY effect source (R11 widened it:
	// an event or trigger switching a thing with no switch is equally dead).
	const effectSources = effectSourcesOf(contentPack);

	effectSources.forEach(([effectsPath, effects]) => {
		effects.forEach((effect, effectIndex) => {
			if (!isBinaryStateEffect(effect)) {
				return;
			}

			const { noun, property } = BINARY_STATE_EFFECT_REQUIREMENTS[effect.type];

			if (itemsById.get(effect.itemId)?.[property] === undefined) {
				failures.push(
					brokenReference(
						`${effectsPath}[${String(effectIndex)}].itemId`,
						effect.itemId,
						`that thing declares no ${noun}, so this effect could never change anything`,
					),
				);
			}
		});
	});

	/*
	 * R11: ending reachability, the §14 promise — effect-existence-shaped. A
	 * declared ending nothing ever selects is authored content no play can
	 * reach (the D31/D47 law, ending-side). Whether a reachable `set_ending`
	 * can actually EXECUTE is simulation-shaped and stays deferred with the
	 * softlock analysis.
	 */
	// D234: a death selects an ending exactly as `set_ending` does, so the
	// manifest's and each foe's `deathEndingId` are selectors too.
	const selectedEndingIds = new Set([
		...effectSources
			.flatMap(([, effects]) => effects)
			.filter((effect) => effect.type === 'set_ending')
			.map((effect) => effect.endingId as string),
		...deathEndingIdsOf(contentPack),
	]);

	// D142: an ending that returns to a checkpoint needs a checkpoint to
	// exist somewhere — a `checkpoint` effect in any source (a checkpoint
	// room's sugar has already become one by now). Without it the restart
	// is dead content: the ending would stand terminal and the declaration
	// would have meant nothing.
	const hasCheckpointSource = effectSources.flatMap(([, effects]) => effects).some((effect) => effect.type === 'checkpoint');

	contentPack.endings.forEach((ending, endingIndex) => {
		if (!selectedEndingIds.has(ending.endingId)) {
			failures.push(
				brokenReference(
					`endings[${String(endingIndex)}].endingId`,
					ending.endingId,
					'no set_ending anywhere selects this ending, so its story can never conclude this way',
				),
			);
		}

		if (ending.restartAt === 'checkpoint' && !hasCheckpointSource) {
			failures.push(
				brokenReference(
					`endings[${String(endingIndex)}].restartAt`,
					ending.endingId,
					'this ending returns to a checkpoint, but nothing in the pack ever takes one — no checkpoint effect and no isCheckpoint room',
				),
			);
		}
	});

	// D246: a branch's arm whose `when` is constant by structure never runs, or always does and shadows the arms after it.
	for (const [effectsPath, effects] of effectSources) {
		effects.forEach((effect, effectIndex) => {
			(effect.type === 'choose' ? effect.arms : []).forEach((arm, armIndex) => {
				const constantFinding = arm.when === undefined ? undefined : findConstantBinaryStateCondition(arm.when, itemsById);

				if (constantFinding !== undefined) {
					failures.push(
						brokenReference(
							`${effectsPath}[${String(effectIndex)}].arms[${String(armIndex)}].when`,
							constantFinding.itemId,
							constantFinding.type === 'switched_on'
								? 'that thing declares no switch, so this arm can never run'
								: 'that thing declares no door, so this arm always runs — it shadows every arm after it',
						),
					);
				}
			});
		});
	}

	/*
	 * R11: a trigger guarded by a CONSTANT binary-state condition is a watcher
	 * that can never fire (constant false) or fires on turn one wearing a
	 * condition that meant nothing (constant true) — the same D56 constants
	 * the description-variant rule catches, watcher-side.
	 */
	contentPack.triggers.forEach((trigger, triggerIndex) => {
		trigger.conditions.forEach((condition, conditionIndex) => {
			const constantFinding = findConstantBinaryStateCondition(condition, itemsById);

			if (constantFinding !== undefined) {
				failures.push(
					brokenReference(
						`triggers[${String(triggerIndex)}].conditions[${String(conditionIndex)}]`,
						constantFinding.itemId,
						constantFinding.type === 'switched_on'
							? 'that thing declares no switch, so this watcher can never fire'
							: 'that thing declares no door, so this guard is always true — the watcher is not watching anything',
					),
				);
			}
		});
	});

	/*
	 * D56: a binary-state CONDITION aimed at an item lacking that property is
	 * constant, and both constants are defects:
	 *
	 * - `switched_on` on a non-switchable item is permanently FALSE, so the
	 *   variant it guards can never speak.
	 * - `container_open` on a doorless item is permanently TRUE ("a thing with
	 *   no door is always open"), so the variant it guards ALWAYS wins —
	 *   stranding every later variant AND the fallback.
	 *
	 * The second is the sharper one, and it is why this rule exists at all: D55
	 * claims a conditional description's fallback is unreachable-proof BY
	 * CONSTRUCTION, and structurally it is — but one always-true condition
	 * shadows it, and structure cannot see that. The claim needed this to be
	 * true rather than nearly true.
	 *
	 * Reach (the D65 debt, paid with D76): originally this checked item
	 * DESCRIPTIONS only, while D65 made rooms conditional and D74 made every
	 * narrative line conditional — the same shadowing defect could hide in an
	 * ask narrative, a lock refusal, a log page, or a voice pool. The check now
	 * finds every conditional-text node by structural walk (`fallback` +
	 * `variants` is the shape's own discriminant; `structural-walk.ts` holds
	 * the rationale), so a new conditional-text home can never escape it.
	 */
	for (const textNode of collectConditionalTextNodes(contentPack)) {
		textNode.variants.forEach((variant, variantIndex) => {
			const constantCondition = findConstantBinaryStateCondition(variant.condition, itemsById);

			if (constantCondition !== undefined) {
				failures.push(
					brokenReference(
						`${textNode.path}.variants[${String(variantIndex)}].condition`,
						constantCondition.itemId,
						constantCondition.type === 'switched_on'
							? 'that thing declares no switch, so this reading can never be chosen'
							: 'that thing declares no door, so this reading is always chosen — it shadows every later variant and the fallback',
					),
				);
			}
		});
	}

	// D140: a fragment's `when` is a reading's condition by another name — a
	// constant one is a sentence that is always appended (which belongs in
	// the description) or never (which is dead content). Found by the same
	// structural walk: `{ text, when }` is the fragment's own discriminant.
	walkDocumentObjects(contentPack, (record, path) => {
		if (record['text'] === undefined || typeof record['when'] !== 'object' || record['when'] === null) {
			return;
		}

		const constantCondition = findConstantBinaryStateCondition(record['when'] as ConditionDefinition, itemsById);

		if (constantCondition !== undefined) {
			failures.push(
				brokenReference(
					`${path}.when`,
					constantCondition.itemId,
					constantCondition.type === 'switched_on'
						? 'that thing declares no switch, so this fragment can never be appended'
						: 'that thing declares no door, so this fragment is always appended — it belongs in the description itself',
				),
			);
		}
	});

	return failures;
};

/**
 * R25: the carry declarations must be able to MEAN something.
 *
 * @remarks
 * Two dead-declaration shapes, both D47-family (authored numbers nothing can
 * ever read):
 *
 * - `bulk` on a non-portable item. Bulk is only ever consulted when a take
 *   weighs the hands, and a thing that cannot be taken never reaches the
 *   scale. The number sits in the JSON meaning nothing — almost certainly an
 *   author who forgot `isPortable`, or changed their mind about portability
 *   and left the bulk behind.
 * - `carryCapacity` in a pack declaring NO portable items. A ceiling over
 *   hands that can never hold anything constrains nothing.
 *
 * @param contentPack - The parsed pack
 * @returns One failure per meaningless declaration, all collected
 */
export const validateCarryDeclarations = (contentPack: ContentPackDefinition): readonly ReferenceBrokenFailure[] => {
	const failures: ReferenceBrokenFailure[] = [];
	const hasPortableItems = contentPack.items.some((item) => item.isPortable);

	contentPack.items.forEach((item, itemIndex) => {
		if (item.bulk !== undefined && !item.isPortable) {
			failures.push(
				brokenReference(
					`items[${String(itemIndex)}].bulk`,
					item.itemId,
					'bulk is only weighed when a take fills the hands, and this item can never be taken — the declaration is dead',
				),
			);
		}

		// D98: the carry VOICES are equally dead on a thing the hands can never
		// lift — nothing non-portable is ever dropped, so nothing non-portable
		// is ever set down or taken back.
		for (const carryChannel of ['dropNarratives', 'retakeNarratives'] as const) {
			if (item[carryChannel] !== undefined && !item.isPortable) {
				failures.push(
					brokenReference(
						`items[${String(itemIndex)}].${carryChannel}`,
						item.itemId,
						`${carryChannel} can only speak for a thing the hands can carry, and this item can never be taken — the lines are dead`,
					),
				);
			}
		}
	});

	if (contentPack.manifest.carryCapacity !== undefined && !hasPortableItems) {
		failures.push(
			brokenReference(
				'manifest.carryCapacity',
				contentPack.manifest.gameId,
				'a carry capacity over a pack with no portable items constrains nothing — the declaration is dead',
			),
		);
	}

	return failures;
};

/** One conditional-text node found by the structural walk, with its variants. */
interface ConditionalTextNode {
	readonly path: string;
	readonly variants: readonly { readonly condition: ConditionDefinition }[];
}

/**
 * Every conditional node in the document, wherever it lives (D65/D76; widened
 * knowingly by R23).
 *
 * @remarks
 * `fallback` + `variants` is the conditional shape's own discriminant, and as
 * of R23 it is shared by exactly TWO schemas ON PURPOSE: `conditionalTextSchema`
 * and `conditionalImageSchema` (a room's plate). The collector reads only each
 * variant's `condition`, which both shapes carry, so a conditional PLATE
 * guarded by a constant condition is caught by the same rule that catches a
 * shadowed description — the reuse D89 priced the schema shape for. Any THIRD
 * schema adopting the shape must want exactly this check; one that does not
 * must choose a different shape, because this walk will find it. The document
 * is schema-parsed before any validation runs, so the variant cast below
 * narrows a shape the parse already proved.
 *
 * @param contentPack - The parsed pack
 * @returns Every conditional node with its path
 */
const collectConditionalTextNodes = (contentPack: ContentPackDefinition): readonly ConditionalTextNode[] => {
	const collected: ConditionalTextNode[] = [];

	walkDocumentObjects(contentPack, (record, path) => {
		if (typeof record['fallback'] === 'string' && Array.isArray(record['variants'])) {
			collected.push({ path, variants: record['variants'] as readonly { readonly condition: ConditionDefinition }[] });
		}
	});

	return collected;
};

/**
 * The first binary-state condition in this tree whose target lacks the property
 * it reads, if any.
 *
 * @remarks
 * Walks the combinators because an always-true `container_open` buried in an
 * `all_of` is exactly as constant as a bare one, and authors nest.
 *
 * @param condition - The authored condition
 * @param itemsById - Every declared item
 * @returns The offending condition, or `undefined`
 */
export const findConstantBinaryStateCondition = (
	condition: ConditionDefinition,
	itemsById: ReadonlyMap<string, ContentPackDefinition['items'][number]>,
): { readonly itemId: string; readonly type: 'container_open' | 'switched_on' } | undefined => {
	switch (condition.type) {
		case 'all_of':
		case 'any_of':
		case 'at_least_of': {
			return condition.conditions
				.map((nested) => findConstantBinaryStateCondition(nested, itemsById))
				.find((found) => found !== undefined);
		}
		case 'container_open': {
			return itemsById.get(condition.itemId)?.openable === undefined ? condition : undefined;
		}
		case 'not': {
			return findConstantBinaryStateCondition(condition.condition, itemsById);
		}
		case 'switched_on': {
			return itemsById.get(condition.itemId)?.switchable === undefined ? condition : undefined;
		}
		default: {
			return undefined;
		}
	}
};

/** The effects that move a named binary state on an item (D50's door pair, D56's switch pair). */
type BinaryStateEffect = Extract<
	EffectDefinition,
	{ type: 'close_container' | 'open_container' | 'switch_off' | 'switch_on' }
>;

/**
 * Does this effect move a binary state on an item?
 *
 * @param effect - Any authored effect
 * @returns Whether it is one of the four
 */
const isBinaryStateEffect = (effect: EffectDefinition): effect is BinaryStateEffect =>
	effect.type === 'close_container' ||
	effect.type === 'open_container' ||
	effect.type === 'switch_off' ||
	effect.type === 'switch_on';

/**
 * What each binary-state effect needs its target to declare, and what to call
 * the missing thing when it does not.
 *
 * @remarks
 * D56 turned D50's hard-coded door check into this table rather than writing
 * the identical rule a second time for switches — the smell §2.1 names. The
 * table is also the honest statement of the rule: an effect that moves a named
 * binary state is dead unless its target declares that state, and a dead effect
 * is authored content nothing can reach.
 */
const BINARY_STATE_EFFECT_REQUIREMENTS: Readonly<
	Record<BinaryStateEffect['type'], { readonly noun: string; readonly property: 'openable' | 'switchable' }>
> = {
	close_container: { noun: 'door', property: 'openable' },
	open_container: { noun: 'door', property: 'openable' },
	switch_off: { noun: 'switch', property: 'switchable' },
	switch_on: { noun: 'switch', property: 'switchable' },
};

/**
 * Does following this item's containment chain come back to where it started?
 *
 * @param startItemId - The item to walk from
 * @param itemsById - Every declared item
 * @returns Whether the chain loops
 */
const hasContainmentCycle = (startItemId: string, itemsById: ReadonlyMap<string, Pick<ItemDefinition, 'containedIn'>>): boolean => {
	const seenItemIds = new Set<string>([startItemId]);
	// D240: a body is never contained, so a chain that reaches one has ended.
	let currentItemId: string | undefined = containerItemIdOf(itemsById.get(startItemId)?.containedIn);

	while (currentItemId !== undefined) {
		if (seenItemIds.has(currentItemId)) {
			return true;
		}

		seenItemIds.add(currentItemId);
		currentItemId = containerItemIdOf(itemsById.get(currentItemId)?.containedIn);
	}

	return false;
};
